import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"

import { CollapsibleSection } from "../../../components/ui/collapsible_section"
import { Input } from "../../../components/ui/input"
import { getForecastDirectory, type DirectoryCustomer, type DirectoryProduct } from "../api/forecast_catalog_api"
import { CustomerSourceBadge } from "./customer_source_badge"

function includes_term(values: Array<string | null | undefined>, term: string): boolean {
  return term === "" || values.some((value) => value?.toLowerCase().includes(term))
}

function case_summary(product: DirectoryProduct): string {
  const { units_per_case: units, packs_per_case: packs } = product
  if (packs && units && units > packs) return `${packs} packs, ${units} units`
  if (units && units > 1) return `${units} units`
  if (packs && packs > 1) return `${packs} units`
  return "—"
}

/** Every product and customer the forecast can use, NetSuite's and the hand-added, in one place. */
export function ForecastDirectorySection() {
  const { data, error, isLoading } = useSWR("forecast-directory", getForecastDirectory, { revalidateOnFocus: false })
  const [search, set_search] = useState("")
  const term = search.trim().toLowerCase()

  const products = useMemo(
    () =>
      (data?.products ?? []).filter((p: DirectoryProduct) =>
        includes_term([p.name, p.netsuite_item_id, p.product_line, p.superseded_by], term),
      ),
    [data, term],
  )
  const customers = useMemo(
    () => (data?.customers ?? []).filter((c: DirectoryCustomer) => includes_term([c.name, c.netsuite_id, ...c.forecast_rows], term)),
    [data, term],
  )

  if (error) return <div className="text-sm text-red-600">Could not load the product and customer list.</div>
  if (isLoading || !data) return <div className="text-sm text-gray-500">Loading products and customers…</div>

  return (
    <div className="space-y-4">
      <Input
        value={search}
        onInput={(e: Event) => set_search((e.currentTarget as HTMLInputElement).value)}
        placeholder="Search products and customers"
        className="max-w-sm"
      />
      <CollapsibleSection
        title="All customers"
        count={customers.length}
        description="NetSuite customers carry their invoices into actuals. Custom customers are forecast rows with no NetSuite account behind them."
        bodyMaxHeight="max-h-[32rem]"
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500 shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">Customer</th>
              <th className="px-4 py-2 text-left">Source</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Forecast rows</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer: DirectoryCustomer) => (
              <tr key={`${customer.source}|${customer.name}`} className="border-t border-gray-100 align-top">
                <td className="px-4 py-2 font-medium text-gray-900">{customer.name}</td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1.5 text-gray-700">
                    <CustomerSourceBadge source={customer.source} />
                    {customer.source === "netsuite" ? `NetSuite ${customer.netsuite_id ?? ""}` : "Custom"}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-700">{customer.customer_type ?? "—"}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{customer.forecast_rows.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleSection>
      <CollapsibleSection
        title="All products"
        count={products.length}
        description="Every catalog product with its NetSuite internal ID. A duplicate says which product it was folded into."
        bodyMaxHeight="max-h-[32rem]"
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500 shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">Product</th>
              <th className="px-4 py-2 text-left">NetSuite ID</th>
              <th className="px-4 py-2 text-left">Item type</th>
              <th className="px-4 py-2 text-right">On hand</th>
              <th className="px-4 py-2 text-left">In a case</th>
              <th className="px-4 py-2 text-left">Product line</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product: DirectoryProduct) => (
              <tr key={product.id} className="border-t border-gray-100 align-top">
                <td className="px-4 py-2">
                  <div className={product.superseded_by ? "text-gray-500" : "font-medium text-gray-900"}>{product.name}</div>
                  {product.superseded_by ? <div className="text-xs text-amber-700">Folded into {product.superseded_by}</div> : null}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-700">{product.netsuite_item_id ?? "—"}</td>
                <td className="px-4 py-2 text-gray-700">{product.item_type ?? "—"}</td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                  {product.quantity_on_hand == null ? "—" : Number(product.quantity_on_hand).toLocaleString()}
                </td>
                <td className="px-4 py-2 text-gray-700">{case_summary(product)}</td>
                <td className="px-4 py-2 text-gray-700">{product.product_line ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleSection>
    </div>
  )
}
